import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Upload,
  Avatar,
  Row,
  Col,
  Divider,
  Select,
  message,
  Space,
  Tabs,
  Switch,
  Rate,
  Tag,
} from 'antd';
import {
  UserOutlined,
  CameraOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  EditOutlined,
  SaveOutlined,
  LockOutlined,
  CarOutlined,
  SafetyOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { deliveryService } from '../services/deliveryService';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [form] = Form.useForm();
  const [vehicleForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await deliveryService.getProfile();
      if (response.data.success && response.data.data) {
        const profileData = response.data.data;
        updateUser(profileData);

        form.setFieldsValue({
          fullName: profileData.name || profileData.full_name || '',
          email: profileData.email || '',
          phone: profileData.phone || profileData.phone_number || '',
          address: profileData.address || '',
          licenseNumber: profileData.license_number || '',
        });

        vehicleForm.setFieldsValue({
          vehicleType: profileData.vehicle_type || '',
          vehicleNumber: profileData.vehicle_number || '',
          vehiclePlate: profileData.vehicle_number || '',
          licenseNumber: profileData.license_number || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      message.error('Failed to load profile data');
    }
  }, [form, vehicleForm, updateUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        fullName: user.name || user.full_name || '',
        email: user.email || '',
        phone: user.phone || user.phone_number || '',
        address: user.address || '',
        licenseNumber: user.license_number || '',
      });

      vehicleForm.setFieldsValue({
        vehicleType: user.vehicle_type || '',
        vehicleNumber: user.vehicle_number || '',
        vehiclePlate: user.vehicle_number || '',
        licenseNumber: user.license_number || '',
      });
    }
  }, [user, form, vehicleForm]);

  const handleProfileUpdate = async (values) => {
    setLoading(true);
    try {
      await deliveryService.updateProfile({
        name: values.fullName,
        email: values.email,
        phone: values.phone,
        address: values.address,
        license_number: values.licenseNumber,
      });

      updateUser({
        ...user,
        name: values.fullName,
        email: values.email,
        phone: values.phone,
        address: values.address,
        license_number: values.licenseNumber,
      });

      message.success('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);
      message.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleUpdate = async (values) => {
    setLoading(true);
    try {
      await deliveryService.updateProfile({
        vehicle_type: values.vehicleType,
        vehicle_number: values.vehicleNumber || values.vehiclePlate,
        license_number: values.licenseNumber,
      });

      updateUser({
        ...user,
        vehicle_type: values.vehicleType,
        vehicle_number: values.vehicleNumber || values.vehiclePlate,
        license_number: values.licenseNumber,
      });

      message.success('Vehicle information updated successfully!');
    } catch (error) {
      console.error('Vehicle update error:', error);
      message.error('Failed to update vehicle information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (_values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      message.success('Password changed successfully!');
      passwordForm.resetFields();
    } catch (error) {
      message.error('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'profile_image',
    action: '/api/delivery/profile',
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    showUploadList: false,
    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG file!');
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
      }
      return isJpgOrPng && isLt2M;
    },
    onChange(info) {
      if (info.file.status === 'done') {
        message.success('Avatar updated successfully!');
        // Refresh profile data to show new image
        fetchProfile();
      } else if (info.file.status === 'error') {
        message.error('Failed to upload avatar. Please try again.');
      }
    },
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>My Profile</Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Manage your personal information, vehicle details, and account settings
      </Paragraph>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Personal Information Tab */}
        <TabPane tab="Personal Information" key="profile">
          <Card>
            <div style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar size={120} icon={<UserOutlined />} />
                    <Upload {...uploadProps}>
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<CameraOutlined />}
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '36px',
                          height: '36px',
                        }}
                      />
                    </Upload>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <Title level={4}>{user?.name || user?.full_name || 'Delivery Partner'}</Title>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <Rate
                        disabled
                        value={parseFloat(user?.average_rating || 0)}
                        style={{ fontSize: '14px' }}
                        allowHalf
                      />
                      <Text type="secondary">({user?.average_rating || '0.0'})</Text>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '0.85rem' }}>
                        {user?.total_deliveries || 0} Deliveries | NPR{' '}
                        {parseFloat(user?.wallet_balance || 0).toFixed(2)}
                      </Text>
                    </div>
                    <Tag
                      color={
                        user?.verification_status === 'verified'
                          ? 'green'
                          : user?.verification_status === 'pending'
                            ? 'orange'
                            : 'red'
                      }
                      style={{ marginTop: '8px' }}
                    >
                      {user?.verification_status === 'verified'
                        ? 'Verified Delivery Partner'
                        : user?.verification_status === 'pending'
                          ? 'Verification Pending'
                          : 'Not Verified'}
                    </Tag>
                  </div>
                </Col>
                <Col xs={24} md={16}>
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleProfileUpdate}
                    disabled={!editing}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="fullName"
                          label="Full Name"
                          rules={[{ required: true, message: 'Please enter your full name!' }]}
                        >
                          <Input prefix={<UserOutlined />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="email"
                          label="Email Address"
                          rules={[
                            { required: true, message: 'Please enter your email!' },
                            { type: 'email', message: 'Please enter a valid email!' },
                          ]}
                        >
                          <Input prefix={<MailOutlined />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="phone"
                          label="Phone Number"
                          rules={[
                            { required: true, message: 'Please enter your phone number!' },
                            { pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' },
                          ]}
                        >
                          <Input
                            type="tel"
                            onKeyPress={(e) =>
                              e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                            }
                            prefix={<PhoneOutlined />}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="dateOfBirth" label="Date of Birth">
                          <Input type="date" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="gender" label="Gender">
                          <Select>
                            <Option value="male">Male</Option>
                            <Option value="female">Female</Option>
                            <Option value="other">Other</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="licenseNumber" label="Driver's License Number">
                          <Input prefix={<IdcardOutlined />} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <Form.Item name="address" label="Address">
                          <Input prefix={<EnvironmentOutlined />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="emergencyContact" label="Emergency Contact">
                          <Input prefix={<UserOutlined />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="emergencyPhone"
                          label="Emergency Phone"
                          rules={[
                            { pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' },
                          ]}
                        >
                          <Input
                            type="tel"
                            onKeyPress={(e) =>
                              e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                            }
                            prefix={<PhoneOutlined />}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {editing && (
                      <div style={{ textAlign: 'right', marginTop: '24px' }}>
                        <Space>
                          <Button onClick={() => setEditing(false)}>Cancel</Button>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<SaveOutlined />}
                          >
                            Save Changes
                          </Button>
                        </Space>
                      </div>
                    )}
                  </Form>

                  {!editing && (
                    <div style={{ textAlign: 'right', marginTop: '24px' }}>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setEditing(true)}
                      >
                        Edit Profile
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Card>
        </TabPane>

        {/* Vehicle Information Tab */}
        <TabPane tab="Vehicle Information" key="vehicle">
          <Card>
            <Title level={4}>Vehicle Details</Title>
            <Paragraph style={{ color: '#666', marginBottom: '24px' }}>
              Keep your vehicle information up to date for insurance and verification purposes
            </Paragraph>

            <Form form={vehicleForm} layout="vertical" onFinish={handleVehicleUpdate}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="vehicleType"
                    label="Vehicle Type"
                    rules={[{ required: true, message: 'Please select vehicle type!' }]}
                  >
                    <Select>
                      <Option value="motorcycle">Motorcycle</Option>
                      <Option value="car">Car</Option>
                      <Option value="van">Van</Option>
                      <Option value="bicycle">Bicycle</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="vehicleMake" label="Vehicle Make">
                    <Input prefix={<CarOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="vehicleModel" label="Vehicle Model">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="vehicleYear" label="Vehicle Year">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="vehiclePlate"
                    label="License Plate"
                    rules={[{ required: true, message: 'Please enter license plate!' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="vehicleColor" label="Vehicle Color">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Insurance Information</Title>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="insuranceCompany" label="Insurance Company">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="insurancePolicy" label="Policy Number">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ textAlign: 'right', marginTop: '24px' }}>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  Update Vehicle Information
                </Button>
              </div>
            </Form>
          </Card>
        </TabPane>

        {/* Security Tab */}
        <TabPane tab="Security" key="security">
          <Card>
            <Title level={4}>Change Password</Title>
            <Paragraph style={{ color: '#666', marginBottom: '24px' }}>
              Update your password to keep your account secure
            </Paragraph>

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              style={{ maxWidth: '400px' }}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password!' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password!' },
                  { min: 8, message: 'Password must be at least 8 characters!' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SafetyOutlined />}
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>

            <Divider />

            <Title level={4}>Two-Factor Authentication</Title>
            <Paragraph style={{ color: '#666', marginBottom: '16px' }}>
              Add an extra layer of security to your account
            </Paragraph>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text strong>Enable 2FA</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                    Use your phone to verify your identity
                  </Text>
                </div>
              </div>
              <Switch defaultChecked={false} />
            </div>
          </Card>
        </TabPane>

        {/* Account Settings Tab */}
        <TabPane tab="Account Settings" key="settings">
          <Card>
            <Title level={4}>Account Preferences</Title>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Email Notifications</Text>
              <div style={{ marginTop: '8px' }}>
                <Space direction="vertical">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>New order assignments</span>
                    <Switch defaultChecked />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Promotional offers</span>
                    <Switch defaultChecked />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Payment confirmations</span>
                    <Switch defaultChecked />
                  </div>
                </Space>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Push Notifications</Text>
              <div style={{ marginTop: '8px' }}>
                <Space direction="vertical">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Order updates</span>
                    <Switch defaultChecked />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Customer messages</span>
                    <Switch defaultChecked />
                  </div>
                </Space>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Availability Settings</Text>
              <div style={{ marginTop: '8px' }}>
                <Space direction="vertical">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Auto-accept orders</span>
                    <Switch defaultChecked={false} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Show me as available</span>
                    <Switch defaultChecked />
                  </div>
                </Space>
              </div>
            </div>

            <Divider />

            <Title level={4}>Account Status</Title>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#52c41a',
                    borderRadius: '50%',
                  }}
                />
                <Text strong>Account Active</Text>
              </div>
              <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                Your account is in good standing and eligible for deliveries
              </Text>
            </div>

            <div style={{ marginTop: '32px' }}>
              <Text strong style={{ color: '#ff4d4f' }}>
                Danger Zone
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Space>
                  <Button danger>Deactivate Account</Button>
                  <Button danger>Delete Account</Button>
                </Space>
              </div>
            </div>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Profile;
